import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Switch } from '@ipfabric/ui-components/Switch/Switch';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type TableColumn,
  type VendorApiType,
  createEnumColumn,
  createTextColumn,
  settingTypes,
} from '@ipfabric/utils';
import { arrayToRecord } from '@ipfabric/utils/fp/arrayToRecord';
import { is } from '@ipfabric/utils/fp/comparison';
import { ___, partialApply } from '@ipfabric/utils/fp/partialApply';
import { prop, propOf } from '@ipfabric/utils/fp/prop';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type {
  TableActionsItem,
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useLocale } from 'containers/App/LocaleContext';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { flow, identity, pipe } from 'fp-ts/lib/function';
import { produce } from 'immer';
import { useMemo, useRef } from 'react';
import type { FC, ReactNode } from 'react';
import { useResponsiveTableButton } from '../../utils/useResponsiveTableButton';
import { useEnabledVendorApiNames } from './helpers/useEnabledVendorApiNames';

interface Props {
  className?: string;
  data: unknown[]; // to be typed once vendors api container page is fully refactored
  onAddVendor: () => void;
  onDeleteVendors: (ids: Set<string>) => void;
  onEditVendor: (id: string) => void;
  onUseGlobalSettings: () => void;
  onVendorEnabledChange: (ids: Set<string>, isEnabled: boolean) => void;
  showUpdateFromGlobalSettingsButton?: boolean;
  titleEndAdornment?: ReactNode;
  noCredentialBox: ReactNode;
}

export const VendorsApiTable: FC<Props> = (props) => {
  const vendorsApiMsg = useLocale().discoverySettings.vendorsApi;
  const columnsMsg = vendorsApiMsg.table.columns;
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { actionItem: overrideActionItem } = useResponsiveTableButton({
    label: 'Overwrite with global settings',
    icon: 'fa-arrows-rotate',
    onClick: props.onUseGlobalSettings,
    containerRef: tableContainerRef,
    size: 'small',
    show: props.showUpdateFromGlobalSettingsButton,
  });

  const requestDeleteVendors = useStableCallback(
    async (ids: Set<string>): Promise<boolean> => {
      if (
        await confirmDeleteDialog.show(getDeleteConfirmDialogMessages(ids), {
          btnConfirmDataTestId: 'vendors-dialog-remove-confirm',
        })
      ) {
        props.onDeleteVendors(ids as Set<string>);
        return true;
      }
      return false;
    },
  );

  const enabledVendorApiNames = useEnabledVendorApiNames();

  const columns: TableColumn<any>[] = useMemo(
    () => [
      createFrontendOnlyColumn({
        key: 'isEnabled',
        title: columnsMsg.isEnabled,
        disabledSort: true,
        width: 81,
        fixedWidth: true,
        className: 'align-middle',
        render: (row) => (
          <div className='inline-flex align-middle'>
            <Switch
              value={row.isEnabled}
              onChange={partialApply(
                props.onVendorEnabledChange,
                new Set([row.id]),
                ___,
              )}
            />
          </div>
        ),
      }),
      createEnumColumn({
        key: 'type',
        title: columnsMsg.type,
        options: pipe(
          enabledVendorApiNames,
          Object.values,
          arrayToRecord(identity, propOf(vendorsApiMsg.vendorTypes)),
        ),
      }),
      createTextColumn({
        key: 'baseUrl',
        title: columnsMsg.baseUrl,
      }),
      createTextColumn({
        key: 'details',
        title: columnsMsg.details,
      }),
      createTextColumn({
        key: 'slug',
        title: columnsMsg.slug,
      }),
      createTextColumn({
        key: 'comment',
        title: columnsMsg.comment,
      }),
      createFrontendOnlyColumn({
        key: 'actions',
        title: 'Actions',
        disabledSort: true,
        render: (row) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              variant='plain'
              dataTestId={`vendors-${row.comment}-edit-icon`}
              icon='fa-pen'
              size='small'
              type='button'
              onClick={() => props.onEditVendor(row.id)}
              disabled={
                !Object.values(settingTypes.vendorApiNames).includes(row.type)
              }
            />
            <TableButton
              dataTestId={`vendors-${row.comment}-remove-icon`}
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={() => requestDeleteVendors(new Set([row.id as string]))}
              type='button'
            />
          </div>
        ),
      }),
    ],
    [
      columnsMsg,
      vendorsApiMsg,
      props.onEditVendor,
      props.onVendorEnabledChange,
      enabledVendorApiNames,
    ],
  );

  const getDeleteConfirmDialogMessages = (vendorsToDelete: Set<string>) =>
    produce(vendorsApiMsg.deleteVendorDialog, (draft) => {
      if (vendorsToDelete.size === 1) {
        draft.subHeader = draft.subHeader.replace(
          '{name}',
          getVendorLabel([...vendorsToDelete][0] as string),
        );
      } else {
        const count = vendorsToDelete.size.toString(10);
        draft.header = draft.headerMultiple.replace('{count}', count);
        draft.subHeader = draft.subHeaderMultiple.replace('{count}', count);
      }
    });
  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: props.data,
  });

  const getVendorLabel = (id: string): string => {
    // missing typings for data
    const vendor: any = props.data.find(flow(prop('id' as never), is(id)));
    if (!vendor) {
      stumble(`vendor id ${id} not found`);
      return '?';
    }
    return (
      vendor.baseUrl ?? vendorsApiMsg.vendorTypes[vendor.type as VendorApiType]
    );
  };

  const bulkActions = useMemo(
    (): TableBulkAction[] => [
      {
        key: 'enable',
        title: vendorsApiMsg.bulkActions.enable,
        onClick: ({ selectedRows }: TableBulkActionClickEvent) => {
          props.onVendorEnabledChange(selectedRows as Set<string>, true);
        },
      },
      {
        key: 'disable',
        title: vendorsApiMsg.bulkActions.disable,
        onClick: ({ selectedRows }: TableBulkActionClickEvent) => {
          props.onVendorEnabledChange(selectedRows as Set<string>, false);
        },
      },
      {
        key: 'delete',
        title: vendorsApiMsg.bulkActions.delete,
        accent: 'negative',
        disableAutoDeselect: true,
        icon: 'fa-trash-can',
        onClick: async ({
          selectedRows,
          deselectAll,
        }: TableBulkActionClickEvent) => {
          if (await requestDeleteVendors(selectedRows as Set<string>)) {
            deselectAll();
          }
        },
      },
    ],
    [vendorsApiMsg, props.onVendorEnabledChange],
  );

  const menuItems = useMemo(
    () => [
      {
        standalone: true,
        elementWithKey: (
          <Button
            dataTestId='vendors-add-vendor'
            icon='fa-plus-circle'
            key='add-new'
            onClick={props.onAddVendor}
            size='small'
            variant='outline'
          >
            Add new
          </Button>
        ),
      } satisfies TableActionsItem,
      overrideActionItem,
    ],
    [props.onAddVendor, overrideActionItem],
  );

  return (
    <>
      <div ref={tableContainerRef} className='flex flex-col flex-1'>
        <Table
          borderless
          bulkActions={bulkActions}
          className={props.className}
          rowHeight={44}
          columns={columns}
          id='vendorsApi'
          loadRowIdsForSelectAll='fromData'
          rowIdField='id'
          title='Vendor APIs'
          titleEndAdornment={props.titleEndAdornment}
          description='For network devices that require discovery via a controller or central
          management tool, define the API endpoint details.'
          noDataBox={props.noCredentialBox}
          menuItems={menuItems}
          {...frontendSimpleFilter}
        />
      </div>
      {confirmDeleteDialog.render()}
    </>
  );
};
